import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "sonner";
import {
  Users2,
  Building2,
  Target,
  TrendingUp,
  Percent,
  Trophy,
  Clock,
  Calendar as CalendarIcon,
  FileText,
  UserCircle2,
  ArrowRight,
  Flame,
} from "lucide-react";
import { api } from "../../lib/api";
import { contactsApi } from "../../lib/contactsApi";
import { dealsApi, constructionApi } from "../../lib/copApi";
import { fmtCompact, fmtMulti, curSymbol, sumByCurrency } from "../../lib/money";

function KpiCard({ tone, icon, label, value, sub, to, testId }) {
  const tones = {
    violet: "from-violet-500 to-fuchsia-500",
    fuchsia: "from-fuchsia-500 to-pink-500",
    sky: "from-sky-500 to-cyan-500",
    emerald: "from-emerald-500 to-teal-500",
    amber: "from-amber-500 to-orange-500",
    red: "from-red-500 to-rose-500",
    indigo: "from-indigo-500 to-violet-500",
    slate: "from-slate-500 to-zinc-500",
  };
  const gradient = tones[tone] || tones.slate;
  const Wrapper = to ? NavLink : "div";
  const wrapperProps = to ? { to } : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={
        "relative block overflow-hidden rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm transition hover:shadow-md " +
        (to ? "cursor-pointer" : "")
      }
      data-testid={testId}
    >
      <div className={`pointer-events-none absolute -top-6 -right-6 h-20 w-20 rounded-full bg-gradient-to-br ${gradient} opacity-10`} />
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br ${gradient} text-white`}>
          {icon}
        </span>
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight text-zinc-950">{value}</div>
      {sub && <div className="mt-0.5 truncate text-[11px] text-zinc-500">{sub}</div>}
    </Wrapper>
  );
}

function Card({ title, sub, action, children, testId }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm" data-testid={testId}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          {sub && <p className="text-[11px] text-zinc-500">{sub}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function CopDashboard() {
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [stages, setStages] = useState([]);
  const [constructions, setConstructions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ct, co, tm, us, dl, sg, cr] = await Promise.all([
          contactsApi.list({ kind: "person" }),
          contactsApi.list({ kind: "company" }),
          api.get("/teams").then((r) => r.data).catch(() => []),
          api.get("/users").then((r) => r.data).catch(() => []),
          dealsApi.list(),
          dealsApi.stages(),
          constructionApi.list({}).catch(() => []),
        ]);
        setContacts(ct || []);
        setCompanies(co || []);
        setTeams(tm || []);
        setUsers((us || []).filter((u) => u.active !== false));
        setDeals(dl || []);
        setStages(sg || []);
        setConstructions(cr || []);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // İnşaat: sorumlu başına müşteri sayısı + ortalama ilerleme (widget).
  const constructionOwners = useMemo(() => {
    const prog = (r) => {
      if (r.progress_percent && r.progress_percent > 0) {
        return Math.min(100, Math.max(0, Number(r.progress_percent) || 0));
      }
      const ms = r.milestones || [];
      if (ms.length === 0) return 0;
      return Math.round((ms.filter((m) => m.completed).length / ms.length) * 100);
    };
    const counts = {};
    const progSum = {};
    let unassigned = 0;
    for (const r of constructions) {
      if (r.assigned_to_user_id) {
        counts[r.assigned_to_user_id] = (counts[r.assigned_to_user_id] || 0) + 1;
        progSum[r.assigned_to_user_id] =
          (progSum[r.assigned_to_user_id] || 0) + prog(r);
      } else {
        unassigned += 1;
      }
    }
    const userById = {};
    for (const u of users) userById[u.id] = u;
    const rows = Object.entries(counts)
      .map(([id, count]) => ({
        id,
        name: userById[id]?.name || "Bilinmeyen",
        count,
        avgProgress: Math.round((progSum[id] || 0) / count),
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "tr"));
    const maxCount = rows.reduce((m, r) => Math.max(m, r.count), 1);
    return { rows, unassigned, total: constructions.length, maxCount };
  }, [constructions, users]);

  // --- Aggregations -------------------------------------------------
  const stats = useMemo(() => {
    const terminalKeys = new Set(stages.filter((s) => s.is_terminal).map((s) => s.key));
    const wonKey = stages.find((s) => s.is_terminal && (s.key.includes("kazan") || s.key === "won"))?.key;
    const lostKey = stages.find((s) => s.is_terminal && (s.key.includes("kayb") || s.key === "lost"))?.key;
    const open = deals.filter((d) => !terminalKeys.has(d.stage));
    const won = wonKey ? deals.filter((d) => d.stage === wonKey) : [];
    const lost = lostKey ? deals.filter((d) => d.stage === lostKey) : [];
    const totalValue = open.reduce((s, d) => s + (Number(d.value) || 0), 0);
    const weightedOf = (d) => {
      const p = d.probability !== null && d.probability !== undefined ? d.probability : 20;
      return (Number(d.value) || 0) * (p / 100);
    };
    const weightedValue = open.reduce((s, d) => s + weightedOf(d), 0);
    const wonValue = won.reduce((s, d) => s + (Number(d.value) || 0), 0);
    // Para birimi bazında toplamlar — karışık TRY/USD tek ₺ altında TOPLANMAZ.
    const totalByCur = sumByCurrency(open);
    const weightedByCur = sumByCurrency(open, weightedOf);
    const wonByCur = sumByCurrency(won);
    const closedCount = won.length + lost.length;
    const winRate = closedCount > 0 ? Math.round((won.length / closedCount) * 100) : null;
    // Owner load (top 5 by open-deal count).
    const byOwner = new Map();
    open.forEach((d) => {
      if (!d.owner_user_id) return;
      byOwner.set(d.owner_user_id, (byOwner.get(d.owner_user_id) || 0) + 1);
    });
    const topOwners = Array.from(byOwner.entries())
      .map(([uid, count]) => {
        const u = users.find((x) => x.id === uid);
        return { user: u, count };
      })
      .filter((x) => x.user)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    // Source distribution (open deals only, so it reflects live pipeline).
    const sourceLabels = {
      website: "Web sitesi",
      referral: "Referans",
      political: "Siyasi Bağlantı",
      cold_call: "Soğuk Arama",
      social_media: "Sosyal Medya",
      __unknown__: "Belirtilmemiş",
    };
    const bySource = new Map();
    open.forEach((d) => {
      const k = d.source || "__unknown__";
      bySource.set(k, (bySource.get(k) || 0) + (Number(d.value) || 0));
    });
    const sourceRows = Array.from(bySource.entries())
      .map(([k, v]) => ({ key: k, label: sourceLabels[k] || k, value: v }))
      .sort((a, b) => b.value - a.value);
    // Top 5 deals by value.
    const topDeals = [...open].sort((a, b) => (b.value || 0) - (a.value || 0)).slice(0, 5);
    // Kapanışa yaklaşanlar — 30 gün içinde.
    const now = new Date();
    const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const closingSoon = open
      .filter((d) => {
        if (!d.expected_close_date) return false;
        const cd = new Date(d.expected_close_date);
        return cd >= now && cd <= in30;
      })
      .sort(
        (a, b) =>
          new Date(a.expected_close_date).getTime() -
          new Date(b.expected_close_date).getTime(),
      )
      .slice(0, 8);
    // Team snapshot: derived managed companies (union of members'
    // assigned_company_ids) per team.
    const teamRows = teams.map((t) => {
      const members = (t.member_ids || [])
        .map((mid) => users.find((u) => u.id === mid))
        .filter(Boolean);
      const cids = new Set();
      members.forEach((m) => (m.assigned_company_ids || []).forEach((c) => cids.add(c)));
      return { team: t, memberCount: members.length, companyCount: cids.size };
    });
    // Pipeline funnel — stage-by-stage adet + toplam değer.
    const funnel = stages.map((s) => {
      const rows = deals.filter((d) => d.stage === s.key);
      return {
        stage: s,
        count: rows.length,
        total: rows.reduce((acc, d) => acc + (Number(d.value) || 0), 0),
        totalByCur: sumByCurrency(rows),
      };
    });
    return {
      totalCompanies: companies.length,
      totalContacts: contacts.length,
      totalTeams: teams.length,
      totalUsers: users.length,
      openCount: open.length,
      totalValue,
      weightedValue,
      totalByCur,
      weightedByCur,
      wonByCur,
      wonCount: won.length,
      wonValue,
      lostCount: lost.length,
      winRate,
      topOwners,
      sourceRows,
      topDeals,
      closingSoon,
      teamRows,
      funnel,
    };
  }, [contacts, companies, teams, users, deals, stages]);

  if (loading) {
    return (
      <div className="p-8 text-center text-sm text-zinc-500">Yükleniyor…</div>
    );
  }

  return (
    <div className="space-y-4" data-testid="cop-dashboard-page">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
          C O P
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-950">
          Yönetim Panosu
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Şirketler, kontaklar, takımlar ve satış pipeline&apos;ının tek ekran özeti.
        </p>
      </div>

      {/* ------- 8-card headline KPI ------- */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8">
        <KpiCard
          tone="sky"
          icon={<Building2 className="h-3.5 w-3.5" />}
          label="Şirket"
          value={stats.totalCompanies}
          sub="Kayıtlı müşteri"
          to="/app/contacts/companies"
          testId="cop-kpi-companies"
        />
        <KpiCard
          tone="violet"
          icon={<UserCircle2 className="h-3.5 w-3.5" />}
          label="Kişi"
          value={stats.totalContacts}
          sub="Kayıtlı kontak"
          to="/app/contacts/people"
          testId="cop-kpi-contacts"
        />
        <KpiCard
          tone="fuchsia"
          icon={<Target className="h-3.5 w-3.5" />}
          label="Açık Fırsat"
          value={stats.openCount}
          sub={`${stats.wonCount + stats.lostCount} kapanan`}
          to="/app/cop/deals"
          testId="cop-kpi-open-deals"
        />
        <KpiCard
          tone="emerald"
          icon={<TrendingUp className="h-3.5 w-3.5" />}
          label="Portföy"
          value={fmtMulti(stats.totalByCur)}
          sub={`Ağırlıklı: ${fmtMulti(stats.weightedByCur)}`}
          testId="cop-kpi-portfolio"
        />
        <KpiCard
          tone="indigo"
          icon={<Users2 className="h-3.5 w-3.5" />}
          label="Takım"
          value={stats.totalTeams}
          sub={`${stats.totalUsers} aktif kullanıcı`}
          to="/app/cop/teams"
          testId="cop-kpi-teams"
        />
        <KpiCard
          tone="amber"
          icon={<Trophy className="h-3.5 w-3.5" />}
          label="Kazanma"
          value={stats.winRate === null ? "—" : `%${stats.winRate}`}
          sub={
            stats.winRate === null
              ? "Henüz kapanış yok"
              : `${fmtMulti(stats.wonByCur)} kazanıldı`
          }
          testId="cop-kpi-winrate"
        />
        <KpiCard
          tone="fuchsia"
          icon={<Clock className="h-3.5 w-3.5" />}
          label="30 Gün İçinde"
          value={stats.closingSoon.length}
          sub="Yaklaşan kapanış"
          testId="cop-kpi-closing"
        />
        <KpiCard
          tone={stats.openCount > 0 && stats.topOwners.length === 0 ? "red" : "slate"}
          icon={<Flame className="h-3.5 w-3.5" />}
          label="Sorumlu"
          value={stats.topOwners.length}
          sub="Aktif satışçı"
          testId="cop-kpi-owners"
        />
      </div>

      {/* ------- Row 1: Sales Funnel + Top Deals ------- */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card
          title="Satış Hunisi"
          sub="Aşama başına adet ve toplam portföy"
          action={
            <NavLink
              to="/app/cop/deals"
              className="text-[11px] font-medium text-violet-600 hover:text-violet-800"
            >
              Pipeline&apos;a git →
            </NavLink>
          }
          testId="cop-funnel-card"
        >
          {stats.funnel.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-center text-xs text-zinc-500">
              Henüz aşama tanımlı değil.
            </div>
          ) : (
            <div className="space-y-2">
              {(() => {
                const maxVal = Math.max(...stats.funnel.map((f) => f.total), 1);
                return stats.funnel.map((f) => {
                  const pct = maxVal > 0 ? Math.round((f.total / maxVal) * 100) : 0;
                  return (
                    <div key={f.stage.key} className="flex items-center gap-3" data-testid={`funnel-row-${f.stage.key}`}>
                      <div className="flex w-32 min-w-0 items-center gap-1.5">
                        <span
                          className="inline-block h-2 w-2 flex-none rounded-full"
                          style={{ backgroundColor: f.stage.color }}
                        />
                        <span className="truncate text-xs font-medium text-zinc-700">
                          {f.stage.label}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="h-3 w-full overflow-hidden rounded-full bg-zinc-100">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: f.stage.color,
                            }}
                          />
                        </div>
                      </div>
                      <div className="w-24 text-right">
                        <div className="text-xs font-semibold text-zinc-900">
                          {fmtMulti(f.totalByCur)}
                        </div>
                        <div className="text-[10px] text-zinc-500">{f.count} fırsat</div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </Card>

        <Card
          title="En Değerli Fırsatlar"
          sub={`Açık portföydeki ilk ${stats.topDeals.length || 5}`}
          testId="cop-top-deals-card"
        >
          {stats.topDeals.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-center text-xs text-zinc-500">
              Açık fırsat yok.
            </div>
          ) : (
            <ul className="space-y-2">
              {stats.topDeals.map((d) => {
                const co = companies.find((c) => c.id === d.company_id);
                const owner = users.find((u) => u.id === d.owner_user_id);
                const stage = stages.find((s) => s.key === d.stage);
                return (
                  <li
                    key={d.id}
                    className="flex items-center gap-2 rounded-md border border-zinc-100 bg-zinc-50/50 p-2"
                    data-testid={`top-deal-${d.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-zinc-900">{d.title}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-zinc-500">
                        {co && <span className="truncate">{co.name}</span>}
                        {owner && (
                          <>
                            <span className="text-zinc-300">·</span>
                            <span className="truncate">{owner.name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-zinc-900">{fmtCompact(d.value, curSymbol(d.currency))}</div>
                      {stage && (
                        <span
                          className="inline-block rounded-full px-1.5 py-0.5 text-[9px] font-medium"
                          style={{
                            background: (stage.color || "#6b7280") + "22",
                            color: stage.color || "#6b7280",
                          }}
                        >
                          {stage.label}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card
          title="Yaklaşan Kapanışlar"
          sub="Önümüzdeki 30 gün"
          testId="cop-closing-soon-card"
        >
          {stats.closingSoon.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-center text-xs text-zinc-500">
              30 gün içinde kapanış planlı fırsat yok.
            </div>
          ) : (
            <ul className="space-y-1">
              {stats.closingSoon.map((d) => {
                const co = companies.find((c) => c.id === d.company_id);
                const dt = new Date(d.expected_close_date);
                const daysLeft = Math.max(
                  0,
                  Math.round((dt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
                );
                return (
                  <li
                    key={d.id}
                    className="flex items-center gap-2 rounded-md border border-zinc-100 bg-white px-2 py-1.5 text-xs"
                    data-testid={`closing-deal-${d.id}`}
                  >
                    <CalendarIcon className="h-3 w-3 flex-none text-zinc-400" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-zinc-900">{d.title}</div>
                      {co && <div className="truncate text-[10px] text-zinc-500">{co.name}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-semibold text-zinc-700">{fmtCompact(d.value, curSymbol(d.currency))}</div>
                      <div
                        className={
                          "text-[10px] font-medium " +
                          (daysLeft <= 7 ? "text-red-600" : "text-zinc-500")
                        }
                      >
                        {daysLeft === 0 ? "Bugün" : `${daysLeft} gün`}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {/* ------- Row 2: Top Owners + Team Snapshot + Source Mix ------- */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card
          title="En Yoğun Sorumlular"
          sub="Açık fırsat sayısına göre"
          testId="cop-top-owners-card"
        >
          {stats.topOwners.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-center text-xs text-zinc-500">
              Sorumlu atanmış açık fırsat yok.
            </div>
          ) : (
            <ul className="space-y-2">
              {stats.topOwners.map(({ user: u, count }) => {
                const max = stats.topOwners[0].count || 1;
                const pct = Math.round((count / max) * 100);
                return (
                  <li key={u.id} className="flex items-center gap-3" data-testid={`owner-${u.id}`}>
                    <div className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-[10px] font-semibold text-white">
                      {(u.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium text-zinc-900">{u.name}</div>
                      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-8 text-right text-xs font-semibold text-zinc-700">
                      {count}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card
          title="Takım Özeti"
          sub={`${stats.totalTeams} takım`}
          action={
            <NavLink to="/app/cop/teams" className="text-[11px] font-medium text-violet-600 hover:text-violet-800">
              Detay →
            </NavLink>
          }
          testId="cop-teams-card"
        >
          {stats.teamRows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-center text-xs text-zinc-500">
              Takım yok.
            </div>
          ) : (
            <ul className="space-y-2">
              {stats.teamRows.map(({ team: t, memberCount, companyCount }) => (
                <li
                  key={t.id}
                  className="flex items-center gap-3 rounded-md border border-zinc-100 bg-zinc-50/50 p-2"
                  data-testid={`team-row-${t.id}`}
                >
                  <div
                    className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-white"
                    style={{ background: `linear-gradient(135deg, ${t.color || "#6366f1"}, #a78bfa)` }}
                  >
                    <Users2 className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-zinc-900">{t.name}</div>
                    <div className="text-[10px] text-zinc-500">
                      {memberCount} üye · {companyCount} şirket
                    </div>
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 text-zinc-300" />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title="Fırsat Kaynağı"
          sub="Açık portföydeki değer dağılımı"
          testId="cop-source-card"
        >
          {stats.sourceRows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-center text-xs text-zinc-500">
              Kaynak bilgisi yok.
            </div>
          ) : (
            <ul className="space-y-2">
              {(() => {
                const total = stats.sourceRows.reduce((s, r) => s + r.value, 0) || 1;
                const palette = ["#8b5cf6", "#f59e0b", "#0ea5e9", "#059669", "#ec4899", "#94a3b8"];
                return stats.sourceRows.map((r, idx) => {
                  const pct = Math.round((r.value / total) * 100);
                  return (
                    <li key={r.key} className="flex items-center gap-3" data-testid={`source-${r.key}`}>
                      <div className="w-24 flex-none text-xs text-zinc-700">{r.label}</div>
                      <div className="flex-1">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${pct}%`, backgroundColor: palette[idx % palette.length] }}
                          />
                        </div>
                      </div>
                      <div className="w-16 text-right text-xs font-semibold text-zinc-800">
                        %{pct}
                      </div>
                    </li>
                  );
                });
              })()}
            </ul>
          )}
        </Card>

        <Card
          title="İnşaat Sorumluları"
          sub={`${constructionOwners.total} müşteri · sorumlu başına dağılım`}
          action={
            <NavLink
              to="/app/cop/construction-tracking"
              className="text-[11px] font-medium text-violet-600 hover:text-violet-800"
            >
              İnşaat Takip →
            </NavLink>
          }
          testId="cop-construction-owners-card"
        >
          {constructionOwners.rows.length === 0 && constructionOwners.unassigned === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-center text-xs text-zinc-500">
              İnşaat takip kaydı yok.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {constructionOwners.rows.map((r) => (
                <li key={r.id}>
                  <NavLink
                    to={`/app/cop/construction-tracking?sorumlu=${r.id}`}
                    data-testid={`construction-owner-${r.id}`}
                    title={`${r.name} — ${r.count} müşteri (listeyi aç)`}
                    className="flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-violet-50"
                  >
                    <div className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-indigo-500 text-[10px] font-semibold text-white">
                      {(r.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-xs font-medium text-zinc-900">
                          {r.name}
                        </span>
                        <span
                          className="flex-none text-[10px] font-medium tabular-nums text-zinc-500"
                          data-testid={`construction-owner-progress-${r.id}`}
                        >
                          ort. ilerleme %{r.avgProgress}
                        </span>
                      </div>
                      {/* Yoğunluk çubuğu — en çok müşterisi olana göre oran */}
                      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-sky-500 to-indigo-500"
                          style={{
                            width: `${Math.max(
                              6,
                              (r.count / constructionOwners.maxCount) * 100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                    <span className="rounded-full bg-zinc-900 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-white">
                      {r.count}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-zinc-300" />
                  </NavLink>
                </li>
              ))}
              {constructionOwners.unassigned > 0 && (
                <li>
                  <NavLink
                    to="/app/cop/construction-tracking?sorumlu=none"
                    data-testid="construction-owner-unassigned"
                    className="flex items-center gap-3 rounded-md bg-amber-50 px-2 py-1.5 ring-1 ring-amber-200 transition-colors hover:bg-amber-100"
                  >
                    <div className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-amber-400 text-[10px] font-semibold text-white">
                      !
                    </div>
                    <div className="min-w-0 flex-1 truncate text-xs font-semibold text-amber-900">
                      Sorumlu atanmamış
                    </div>
                    <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-white">
                      {constructionOwners.unassigned}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-amber-400" />
                  </NavLink>
                </li>
              )}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
